import type { SessionTokens } from '../types/session';

export const SESSION_KEY = 'tokens';
export const EXPIRED_MESSAGE = 'Your session has ended or access is no longer available. Please sign in again.';
export class AuthenticationEnded extends Error {}
export class AuthenticationUnavailable extends Error {}

type StoredSession = SessionTokens & { generation: string };
type Transport = (refreshToken: string) => Promise<SessionTokens>;
export type SessionEnvironment = {
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  lock: <T>(work: () => Promise<T>) => Promise<T>;
  publish: () => void;
  changed: (session: StoredSession | null, message?: string) => void;
};

export class SessionAuth {
  private refreshing: Promise<StoredSession> | null = null;
  constructor(
    private env: SessionEnvironment,
    private transport: Transport
  ) {}

  read(): StoredSession | null {
    try {
      const value = JSON.parse(this.env.storage.getItem(SESSION_KEY) || 'null');
      return value?.token && value?.refresh_token && value?.session_id && value?.generation ? value : null;
    } catch {
      return null;
    }
  }

  snapshot() {
    return this.env.storage.getItem(SESSION_KEY);
  }

  login(tokens: SessionTokens, expected: string | null) {
    if (this.snapshot() !== expected) throw new AuthenticationEnded('Authentication changed. Please try signing in again.');
    const session = { ...tokens, generation: crypto.randomUUID() };
    this.commit(session);
    return session;
  }

  private commit(session: StoredSession) {
    this.env.storage.setItem(SESSION_KEY, JSON.stringify(session));
    this.env.changed(session);
    this.env.publish();
  }

  clear(message?: string, expectedGeneration = this.read()?.generation) {
    if (this.read()?.generation !== expectedGeneration) return;
    // Keep a credential-free revision so a late login started before logout cannot win.
    this.env.storage.setItem(SESSION_KEY, JSON.stringify({ generation: crypto.randomUUID(), loggedOut: true, message }));
    this.env.changed(null, message);
    this.env.publish();
  }

  sync() {
    const session = this.read();
    let message: string | undefined;
    if (!session) {
      try {
        const saved = JSON.parse(this.snapshot() || '{}');
        if (saved?.token || saved?.refresh_token) {
          this.clear('The sign-in system has been updated. Please sign in again.');
          return;
        }
        message = saved?.message;
      } catch {
        /* Invalid legacy storage. */
      }
    }
    this.env.changed(session, message);
  }

  expired(session: SessionTokens) {
    return !Number.isFinite(Date.parse(session.access_expires_at)) || Date.parse(session.access_expires_at) <= Date.now() + 5000;
  }

  async access() {
    const session = this.read();
    if (!session) {
      this.clear(EXPIRED_MESSAGE);
      throw new AuthenticationEnded(EXPIRED_MESSAGE);
    }
    return this.expired(session) ? this.refresh(session.token) : session;
  }

  refresh(failedToken?: string): Promise<StoredSession> {
    if (this.refreshing) return this.refreshing;
    const original = this.read();
    if (!original) return Promise.reject(new AuthenticationEnded(EXPIRED_MESSAGE));
    this.refreshing = this.env
      .lock(async () => {
        // Read again inside the cross-tab lock: another tab may have rotated already.
        const current = this.read();
        if (!current || current.generation !== original.generation) throw new AuthenticationEnded('Authentication changed.');
        if (current.token !== (failedToken || original.token)) return current;
        try {
          // Deliberately no network retry: a delayed retry could exceed the server's five-second replay window.
          const tokens = await this.transport(current.refresh_token);
          const latest = this.read();
          if (!latest || latest.generation !== current.generation || latest.refresh_token !== current.refresh_token) {
            throw new AuthenticationEnded('Authentication changed.');
          }
          const session = { ...tokens, generation: current.generation };
          this.commit(session);
          return session;
        } catch (error) {
          if ((error as { response?: { status: number } }).response?.status === 401) {
            this.clear(EXPIRED_MESSAGE, current.generation);
            throw new AuthenticationEnded(EXPIRED_MESSAGE);
          }
          throw error;
        }
      })
      .finally(() => {
        this.refreshing = null;
      });
    return this.refreshing;
  }

  async request<T>(send: (token: string) => Promise<T>): Promise<T> {
    const session = await this.access();
    const assertCurrent = () => {
      if (this.read()?.generation !== session.generation) throw new AuthenticationEnded('Authentication changed.');
    };
    assertCurrent();
    try {
      const result = await send(session.token);
      assertCurrent();
      return result;
    } catch (error) {
      assertCurrent();
      if ((error as { response?: { status: number } }).response?.status !== 401) throw error;
      const refreshed = await this.refresh(session.token);
      assertCurrent();
      try {
        const result = await send(refreshed.token);
        assertCurrent();
        return result;
      } catch (retryError) {
        assertCurrent();
        if ((retryError as { response?: { status: number } }).response?.status === 401) {
          this.clear(EXPIRED_MESSAGE, session.generation);
          throw new AuthenticationEnded(EXPIRED_MESSAGE);
        }
        throw retryError;
      }
    }
  }
}

// Fallback for browsers without Web Locks. An IndexedDB read/write transaction
// serializes tabs; keep-alive reads hold the transaction until the operation finishes.
export async function browserSessionLock<T>(work: () => Promise<T>): Promise<T> {
  if (navigator.locks?.request) return navigator.locks.request('hackerest-session-refresh', work);
  if (typeof indexedDB === 'undefined') {
    throw new AuthenticationUnavailable(
      'This browser cannot safely renew sessions. Please sign in again or use a browser with Web Locks or IndexedDB.'
    );
  }
  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open('hackerest-auth-lock', 1);
    request.onupgradeneeded = () => request.result.createObjectStore('mutex');
    request.onsuccess = () => resolve(request.result);
    request.onblocked = () => reject(new AuthenticationUnavailable('Session storage is busy. Close older tabs and retry.'));
    request.onerror = () =>
      reject(new AuthenticationUnavailable('Session coordination is unavailable. Please allow browser storage or sign in again.'));
  });
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction('mutex', 'readwrite');
    const store = transaction.objectStore('mutex');
    let finished = false;
    let started = false;
    let result: T;
    let failure: unknown;
    const keepAlive = () => {
      const request = store.get('refresh');
      request.onsuccess = () => {
        if (!started) {
          started = true;
          work().then(
            value => {
              result = value;
              finished = true;
            },
            error => {
              failure = error;
              finished = true;
            }
          );
        }
        if (!finished) keepAlive();
      };
    };
    transaction.oncomplete = () => {
      db.close();
      failure ? reject(failure) : resolve(result);
    };
    transaction.onabort = () => {
      db.close();
      reject(new AuthenticationUnavailable('Session coordination was interrupted. Please retry.'));
    };
    keepAlive();
  });
}
